import { Bell, CheckCircle2, ClipboardList, CreditCard } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCourseSignups, getNotificationReadIds, getTransactions, markNotificationRead } from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';
import { formatDateTime } from '../../utils/date';
import { buildDashboardNotifications } from '../../utils/dashboardNotifications';
import { useAuth } from '../../hooks/useAuth';

export function DashboardNotificationsPage() {
  const { activeTenant } = useTenant();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [readIds, setReadIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!activeTenant?.id) return;
      try {
        const [signups, transactions] = await Promise.all([getCourseSignups(activeTenant.id), getTransactions(activeTenant.id)]);
        let storedReadIds = [];
        try {
          storedReadIds = await getNotificationReadIds(user?.uid, activeTenant.id);
        } catch (error) {
          console.warn('Unable to load notification read status. Deploy the latest Firestore rules to enable sync.', error);
        }
        const nextItems = buildDashboardNotifications(signups, transactions);
        if (mounted) {
          setItems(nextItems);
          setReadIds(new Set(storedReadIds));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [activeTenant?.id, user?.uid]);

  const unread = useMemo(() => {
    return items.filter((item) => !readIds.has(item.id));
  }, [items, readIds]);

  function handleNotificationClick(notificationId) {
    if (!user?.uid || !activeTenant?.id || readIds.has(notificationId)) return;
    setReadIds((currentIds) => new Set(currentIds).add(notificationId));
    window.dispatchEvent(new CustomEvent('dashboard-notification-read', {
      detail: { tenantId: activeTenant.id, notificationId },
    }));
    markNotificationRead(user.uid, activeTenant.id, notificationId).catch((error) => {
      console.error('Unable to save notification read status', error);
    });
  }

  return <section className="dashboard-notifications-page">
    <header className="dashboard-notifications-header"><div><span>Workspace activity</span><h2>Notifications</h2><p>Stay up to date with new applications and fee payments.</p></div><div className="dashboard-notification-summary"><Bell size={18} /><strong>{unread.length}</strong><small>unread</small></div></header>
    <div className="dashboard-notification-list">
      {loading ? <div className="dashboard-notification-empty">Loading notifications…</div> : items.length ? items.map((item) => <button className={`dashboard-notification-item${readIds.has(item.id) ? ' is-read' : ''}`} type="button" key={item.id} onClick={() => handleNotificationClick(item.id)}><span className={item.type === 'payment' ? 'is-payment' : 'is-application'}>{item.type === 'payment' ? <CreditCard size={17} /> : <ClipboardList size={17} />}</span><div><strong>{item.title}</strong><p>{item.name} · {item.detail}</p><small>{formatDateTime(item.date)}</small></div><CheckCircle2 size={16} /></button>) : <div className="dashboard-notification-empty"><Bell size={26} /><strong>No notifications yet</strong><span>New applications and fee payments will appear here.</span></div>}
    </div>
    <Link className="dashboard-notifications-back" to="/dashboard">Back to dashboard</Link>
  </section>;
}
